<template>
  <div class="listing-table">
    <div class="listing-options">
      <div class="custom-dropdown">
        <p class="label">{{ $t('listingPage.table.timeLabel') }}</p>
        <v-select class="select-dropdown"
                  v-model="selectedMonth"
                  :options="monthsList"
                  :reduce="month => month.ordinal"
                  label="name"
                  :filterable="false"
                  :searchable="false"
                  @option:selected="selectMonth"></v-select>
      </div>
      <div class="custom-dropdown">
        <p class="label">{{ $t('listingPage.table.trafficTypeLabel') }}</p>
        <v-select class="select-dropdown"
                  v-model="selectedDataType"
                  :options="dataTypeOptions"
                  :filterable="false"
                  :searchable="false"
                  @option:selected="resetPagination"></v-select>
      </div>
      <base-checkbox v-model="payableServicesOnly" class="payable-checkbox">
        <span class="checkbox-text">{{ $t('listingPage.table.billableServicesLabel') }}</span>
      </base-checkbox>
    </div>
    <p class="small-note note">{{ $t('listingPage.table.roamingNote') }}</p>
    <div class="table-preheader">
      {{ $t('listingPage.table.totalExpensesLabel') }} <span class="emphasized-text">{{ $props.totalExpense }}</span>
    </div>
    <base-table :data="tableData" :columns="columnDefinition" class="rounded-table">
      <div slot="no-content" class="no-content">
        <p class="description" v-if="!payableServicesOnly">{{ $t('listingPage.table.noContentDefault') }}</p>
        <p class="description" v-else>{{ $t('listingPage.table.noContentBillable') }}</p>
      </div>
    </base-table>
    <table-pagination ref="pagination"
                      :total-pages="totalPages"
                      :current-page="currentPage"
                      class="pagination"
                      :page-options="[5, 10, 15]"
                      @page-change="goToPage"
                      @rows-change="setRowsPerPage"
                      @page-jump="goToPage"/>
  </div>
</template>

<script>
import BaseTable from '@/components/base/table/base-table'
import TablePagination from '@/components/base/table/table-pagination'

const availableMonths = [
  { name: 'Januar', ordinal: 1 },
  { name: 'Februar', ordinal: 2 },
  { name: 'Mart', ordinal: 3 },
  { name: 'April', ordinal: 4 },
  { name: 'Maj', ordinal: 5 },
  { name: 'Jun', ordinal: 6 },
  { name: 'Jul', ordinal: 7 },
  { name: 'Avgust', ordinal: 8 },
  { name: 'Septembar', ordinal: 9 },
  { name: 'Oktobar', ordinal: 10 },
  { name: 'Novembar', ordinal: 11 },
  { name: 'Decembar', ordinal: 12 }
]
export default {
  name: 'listing-table',
  components: { TablePagination, BaseTable },
  props: {
    isPrepaid: Boolean,
    data: {
      type: Array,
      default: () => {
        return []
      }
    },
    totalExpense: String
  },
  data () {
    return {
      currentPage: 1,
      rowsPerPage: 5,
      selectedDataType: 'Sve',
      dataTypeOptions: ['Sve', 'Poziv', 'SMS', 'Internet', 'Parking', 'Roaming', 'Ostalo'],
      selectedMonth: 0,
      monthsList: [],
      payableServicesOnly: false,
      columnDefinition: [
        {
          key: 'formattedDate',
          label: 'Datum i vrijeme',
          colClass: 'nowrap listing-date-col'
        },
        {
          key: 'trafficType',
          label: 'Vrsta saobraćaja',
          colClass: 'traffic-type-col'
        },
        {
          key: 'target',
          label: 'Broj telefona',
          colClass: 'phone-number-col'
        },
        {
          key: 'network',
          label: 'Operater'
        },
        {
          key: 'formattedAmount',
          label: 'Količina'
        },
        {
          key: 'formattedPrice',
          label: 'Cijena',
          colClass: 'price-col'
        }
      ]
    }
  },
  computed: {
    tableData () {
      return this.filteredData.slice((this.currentPage - 1) * this.rowsPerPage, this.currentPage * this.rowsPerPage)
    },
    filteredData () {
      if (this.payableServicesOnly) {
        if (this.selectedDataType === 'Sve') return this.$props.data.filter(entry => Number(entry.price) > 0)
        return this.$props.data.filter(entry => entry.trafficType === this.selectedDataType && Number(entry.price) > 0)
      }
      if (this.selectedDataType === 'Sve') return this.$props.data
      return this.$props.data.filter(entry => entry.trafficType === this.selectedDataType)
    },
    totalPages () {
      return Math.ceil(this.filteredData.length / this.rowsPerPage)
    }
  },
  methods: {
    goToPage (page) {
      if (page === 'next') {
        if (this.currentPage + 1 <= this.totalPages) {
          this.currentPage++
        }
      } else if (page === 'prev') {
        if (this.currentPage - 1 >= 1) {
          this.currentPage--
        }
      } else {
        this.currentPage = page
      }
    },
    setRowsPerPage (rows) {
      this.currentPage = 1
      this.$refs.pagination.resetInternalPageNumber()
      this.rowsPerPage = rows
    },
    populateMonthsList () {
      const monthCount = this.$props.isPrepaid ? 2 : 5
      const prepaidMonthOffset = this.$props.isPrepaid ? 0 : 1
      let thisMonth = new Date().getMonth() + 1 - prepaidMonthOffset
      this.selectedMonth = thisMonth > 0 ? thisMonth : 12
      for (let i = 1; i <= monthCount; i++) {
        if (thisMonth < 1) thisMonth = 12
        const month = availableMonths.find(singleMonth => singleMonth.ordinal === thisMonth)
        this.monthsList.push(month)
        thisMonth--
      }
    },
    resetPagination () {
      this.currentPage = 1
      this.$refs.pagination.resetInternalPageNumber()
    },
    selectMonth () {
      this.$emit('select-month', this.selectedMonth)
      this.resetPagination()
    }
  },
  watch: {
    payableServicesOnly () {
      this.resetPagination()
    }
  },
  mounted () {
    this.populateMonthsList()
  }
}
</script>

<style scoped lang="scss">
.rounded-table {
  border-radius: 0 0 0.5rem 0.5rem;
}

.table-preheader {
  background: $white;
  border-radius: 0.5rem;
  padding: 1.6rem;
  color: $text;
  font-weight: normal;
  font-size: 1.4rem;
  margin-bottom: 1.6rem;

  @media (map_get($breakpoints, sm)) {
    font-size: 1.6rem;
    margin-bottom: 0;
    border-radius: 0.5rem 0.5rem 0 0;
    border-bottom: 1px solid $grey;
    box-shadow: 5px 0 20px 0 rgba(0, 0, 0, 0.06);
    position: relative;
    z-index: 1;
  }

  .emphasized-text {
    font-weight: bold;
    color: $primary;
  }
}

.listing-options {
  margin: 2.4rem 0 1.6rem;
  display: flex;
  flex-direction: column;

  @media (map_get($breakpoints, sm)) {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
  }

  .custom-dropdown {
    margin-bottom: 1.6rem;

    @media (map_get($breakpoints, sm)) {
      flex: 1 1 200px;
      max-width: 200px;
      margin-right: 1.6rem;
      margin-bottom: 0;
    }

    .label {
      color: $dark-grey;
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
      cursor: default;
    }
  }

  .payable-checkbox {
    margin-bottom: 1rem;
    margin-left: 0.8rem;
  }

  .checkbox-text {
    font-size: 1.6rem;
    font-weight: bold;
  }
}

.note {
  margin-bottom: 4rem;
}

.pagination {
  margin-top: 1.6rem;
}

.no-content {
  padding: 4rem;

  .description {
    text-align: center;
    font-size: 24px;
    color: $dark-grey;
    margin: 0;
  }
}
</style>
